/* Copy da listagem de empresas: header, CTA de criação (super_admin), estado de erro e o botão de
   paginação. Filtros e tabela são componentes externos (`components/companies/*`) — fora do
   escopo desta área. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface CompaniesListContent {
  readonly title: string;
  readonly description: string;
  readonly createOrg: string;
  readonly loadFailed: string;
  readonly loadMore: string;
  readonly bulk: {
    readonly selected: (count: number) => string;
    readonly block: string;
    readonly unblock: string;
    readonly clear: string;
    readonly blockTitle: string;
    readonly unblockTitle: string;
    readonly description: (action: string, count: number) => string;
    readonly success: (count: number) => string;
    readonly partial: (success: number, failed: number) => string;
    readonly error: string;
  };
}

const COMPANIES_LIST_CONTENT: Bilingual<CompaniesListContent> = {
  'pt-BR': {
    title: 'Empresas',
    description: 'Organizações cadastradas — governança, planos e bloqueios.',
    createOrg: 'Nova organização',
    loadFailed: 'Falha ao carregar empresas.',
    loadMore: 'Carregar mais',
    bulk: {
      selected: (count) => `${count} empresa(s) selecionada(s)`,
      block: 'Bloquear',
      unblock: 'Desbloquear',
      clear: 'Limpar seleção',
      blockTitle: 'Bloquear empresas selecionadas',
      unblockTitle: 'Desbloquear empresas selecionadas',
      description: (action, count) =>
        `${action} ${count} empresa(s) com o mesmo motivo? O resultado será informado por item.`,
      success: (count) => `Ação aplicada a ${count} empresa(s).`,
      partial: (success, failed) => `${success} concluída(s); ${failed} com falha.`,
      error: 'Falha ao aplicar a ação em lote.',
    },
  },
  'en-US': {
    title: 'Companies',
    description: 'Registered organizations — governance, plans, and blocks.',
    createOrg: 'New organization',
    loadFailed: 'Failed to load companies.',
    loadMore: 'Load more',
    bulk: {
      selected: (count) => `${count} company(ies) selected`,
      block: 'Block',
      unblock: 'Unblock',
      clear: 'Clear selection',
      blockTitle: 'Block selected companies',
      unblockTitle: 'Unblock selected companies',
      description: (action, count) =>
        `${action} ${count} company(ies) with the same reason? Results are reported per item.`,
      success: (count) => `Action applied to ${count} company(ies).`,
      partial: (success, failed) => `${success} completed; ${failed} failed.`,
      error: 'Failed to apply the bulk action.',
    },
  },
};

export const useCompaniesListContent = (): CompaniesListContent =>
  useBilingual(COMPANIES_LIST_CONTENT);
